
import Vue, { PropType } from "vue";
import { useCases } from "@/core";
import { EProfessionalApprovalStatus } from "@/domain/enums/EProfessionalApprovalStatus";
import { EProfessionalRejectionStep } from "@/domain/enums/EProfessionalRejectionStep";
import { ProfessionalViewModel } from "@/presentation/viewmodels/ProfessionalViewModel";
import { PROFESSIONAL_REJECTION_STEP_OPTIONS } from "@/presentation/constants/professionalRejectionStepLabels";
import { toggleModal } from "@/utils/Helpers";
import { maxLength, required } from "vuelidate/lib/validators";

export default Vue.extend({
  name: "ProfessionalApproval",
  props: {
    // Já vem formatado pro viewmodel pelo ProfessionalInfo — approved/
    // rejection_step/rejection_reason são campos do próprio Professional
    // (mesmo recurso, sem domínio separado de "aprovação"). professional.user_id
    // é o id do usuário, usado no PUT .../approve (o backend reverteu a
    // rota pra aceitar o id do usuário de novo, não o do registro do profissional).
    professional: {
      type: Object as PropType<ProfessionalViewModel>,
      required: true,
    },
  },
  data() {
    return {
      modalId: "professionalApprovalRejectModal",
      submitting: false,
      rejectionStepOptions: PROFESSIONAL_REJECTION_STEP_OPTIONS,
      form: {
        rejection_step: "",
        rejection_reason: "",
      },
    };
  },
  validations: {
    form: {
      rejection_step: { required },
      rejection_reason: { required, maxLength: maxLength(500) },
    },
  },
  computed: {
    cardBorderClass() {
      if (this.professional.approved === EProfessionalApprovalStatus.APPROVED) {
        return "border-success";
      }
      if (this.professional.approved === EProfessionalApprovalStatus.REJECTED) {
        return "border-danger";
      }
      return "border-warning";
    },
    statusSubtitle() {
      if (this.professional.approved === EProfessionalApprovalStatus.APPROVED) {
        return "Disponível para os usuários realizarem consultas na plataforma.";
      }
      if (this.professional.approved === EProfessionalApprovalStatus.REJECTED) {
        return "O profissional recebeu a reprovação e não pode receber consultas até corrigir os pontos indicados.";
      }
      return "Aguardando aprovação para começar a receber consultas.";
    },
  },
  methods: {
    handleApprove() {
      this.$eventBus.$emit("showConfirmationAlert", {
        title: "Aprovar profissional",
        message:
          "Deseja aprovar este profissional? Ele aparecerá disponível para os usuários realizarem consultas na plataforma.",
        confirmCallback: async () => {
          await this.submitApproval({
            approved: EProfessionalApprovalStatus.APPROVED,
          });
        },
      });
    },
    openRejectModal() {
      this.form.rejection_step = "";
      this.form.rejection_reason = "";
      this.$v.$reset();
      toggleModal(this.modalId);
    },
    async handleReject() {
      this.$v.form.$touch();
      if (this.$v.form.$invalid) {
        return;
      }
      // Fecha o modal ANTES de submeter: submitApproval emite "onUpdated",
      // que faz o pai (UserDetailController) refazer o GET e trocar o
      // ProfessionalInfo por um LoadingContainer (v-if/v-else) enquanto
      // carrega. Se o modal ainda estivesse aberto nesse momento, esse
      // componente (e o botão oculto que o toggleModal usa pra fechar)
      // seria desmontado no meio do processo — o backdrop do Bootstrap
      // fica órfão na tela (ele é injetado direto no body, fora da árvore
      // do Vue) e trava a interface.
      toggleModal(this.modalId);
      await this.submitApproval({
        approved: EProfessionalApprovalStatus.REJECTED,
        rejection_step: this.form.rejection_step as EProfessionalRejectionStep,
        rejection_reason: this.form.rejection_reason,
      });
    },
    async submitApproval(params) {
      try {
        this.submitting = true;
        await useCases.updateProfessionalApprovalUseCase.execute({
          user_id: this.professional.user_id,
          ...params,
        });
        this.$eventBus.$emit("showToast", {
          title:
            params.approved === EProfessionalApprovalStatus.APPROVED
              ? "Profissional aprovado"
              : "Profissional reprovado",
          msg:
            params.approved === EProfessionalApprovalStatus.APPROVED
              ? "O profissional foi aprovado com sucesso"
              : "O profissional foi reprovado e notificado sobre suas pendências",
        });
        this.$emit("onUpdated");
      } catch (e) {
        console.log(e);
        this.$eventBus.$emit("showToast", {
          type: "danger",
          title: "Erro",
          msg: "Não foi possível atualizar a aprovação do profissional",
        });
      } finally {
        this.submitting = false;
      }
    },
  },
});
